/**
 * Observable store for the "Voice & Music" master volume.
 *
 * Two facts make this its own module:
 *
 * 1. The voice + music audio chain lives across **two separate
 *    AudioContexts** (`MusicPlayer` for the host's own jukebox playback,
 *    `SpatialAudioController` for everything LiveKit forwards). GainNodes
 *    can't be shared across AudioContexts, so each subsystem owns its
 *    own gain and *subscribes* to a shared volume state instead of
 *    sharing a single master node.
 *
 * 2. The Sound FX slider (driven by `SoundManager`) is intentionally
 *    independent — muting UI chimes shouldn't kill voice, and vice
 *    versa.
 *
 * Persisted in localStorage; defaults to full (1.0) on first run so a
 * new user isn't surprised by quiet voice.
 */

const VOLUME_KEY = "mythglade_voice_volume";
const MUTED_KEY = "mythglade_voice_muted";

type Listener = (gain: number) => void;

let _volume = parseFloat(localStorage.getItem(VOLUME_KEY) ?? "1.0");
let _muted = localStorage.getItem(MUTED_KEY) === "1";
const listeners = new Set<Listener>();

function effective(): number {
  return _muted ? 0 : Math.max(0, Math.min(1, _volume));
}

function fire(): void {
  const v = effective();
  for (const cb of listeners) cb(v);
}

export const VoiceVolume = {
  setVolume(v: number): void {
    _volume = Math.max(0, Math.min(1, v));
    localStorage.setItem(VOLUME_KEY, String(_volume));
    fire();
  },

  getVolume(): number {
    return _volume;
  },

  setMuted(m: boolean): void {
    _muted = m;
    localStorage.setItem(MUTED_KEY, m ? "1" : "0");
    fire();
  },

  isMuted(): boolean {
    return _muted;
  },

  /** Current product of (muted ? 0 : volume). Driven into each gain node. */
  getEffectiveGain(): number {
    return effective();
  },

  /**
   * Subscribe to volume changes. The callback is invoked immediately
   * with the current gain so subscribers don't have to bootstrap. Call
   * the returned function to unsubscribe.
   */
  subscribe(cb: Listener): () => void {
    listeners.add(cb);
    cb(effective());
    return () => listeners.delete(cb);
  },
};
